/**
 * Copyright 2017 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import React from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import FaPlusCircle from 'react-icons/lib/fa/plus-circle';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { getThumbnailUrl } from '~services/utils'; // eslint-disable-line

import messages from './messages/PaidFeatures_messages';

import PortfolioSlide from './PortfolioSlide';

const propTypes = {
  addSlide: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleImageUpload: PropTypes.func.isRequired,
  removeSlide: PropTypes.func.isRequired,
  slides: PropTypes.array.isRequired,
  country: PropTypes.string.isRequired,
  identifier: PropTypes.string.isRequired,
};

const PortfolioSlides = ({
  addSlide,
  editMode,
  handleChange,
  handleImageUpload,
  removeSlide,
  slides,
  country,
  identifier,
}) => {
  const getImageUpdater = slideId => (acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length) {
      handleImageUpload({
        slideId,
        country,
        identifier,
        file: acceptedFiles[0]
      });
    }

    if (rejectedFiles.length) {
      console.log('Wrong file type given.');
    }
  };

  return (
    <React.Fragment>
      {(slides.length === 0 && editMode) && (
        <FormattedMessage
          {...messages.noSlidesAndEdit}
          info="You have no pages... Click button below"
        />
      )}

      {(slides.length === 0 && !editMode) && (
        <FormattedMessage
          {...messages.noSlidesAndNoEdit}
          info="You have no pages... Activate edit mode"
        />
      )}

      {editMode && (
        <SlideHelp>
          <FormattedMessage
            {...messages.guideTextOrdering}
            info="Guide text for ordering brochure pages"
          />
        </SlideHelp>
      )}

      <DragDropContext
        onDragEnd={result => {
          handleChange({
            field: 'order_no',
            slideId: parseInt(result.draggableId, 10),
            value: result.destination.index,
          })
        }}
      >
        <Droppable droppableId='portfolioSlides'>
          {provided => (
            <SlidesContainer
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {slides
                .sort((a, b) => a.order_no - b.order_no)
                .map((slide, index) => (
                  <PortfolioSlide
                    editMode={editMode}
                    handleChange={handleChange}
                    handleImageUpload={getImageUpdater(slide.id)}
                    index={index}
                    removeSlide={removeSlide}
                    slide={slide}
                  />
                ))}
              {provided.placeholder}
            </SlidesContainer>
          )}
        </Droppable>
      </DragDropContext>

      {editMode && (
        <SlideHelp>
          <FormattedMessage
            {...messages.guideText}
            info="Guide text for adding brochure pages"
          />
        </SlideHelp>
      )}

      {editMode && (
        <SlideControls>
          <AddSlideIcon onClick={() => addSlide()} />
        </SlideControls>
      )}
    </React.Fragment>
  );
};

PortfolioSlides.propTypes = propTypes;

const AddSlideIcon = styled(FaPlusCircle)`
  color: ${props => props.theme['grey-dark']};
  font-size: 36pt;
`;

const SlideControls = styled.div`
  display: flex;
  justify-content: center;
  margin: 48px 0px;
`;

const SlideHelp = styled.p`
  background-color: ${props => props.theme.beige};
  border-radius: 6px;
  font-size: 10pt;
  line-height: 13pt;
  margin: 0px;
  padding: 12px;
`;

const SlidesContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 0;
`;

export default PortfolioSlides;
